import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Pencil, Trash2, X, Save, Loader2, Globe,
  Calendar, MapPin, ArrowLeft, Play, Image, Info,
  Upload, CheckCircle2, FolderOpen, Film, Link2,
  Eye, EyeOff, Sparkles, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import axios from 'axios';
import MediaPickerModal from '@/components/MediaPickerModal';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api';
const STORAGE_BASE = API_BASE.replace(/\/api$/, '');
const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${STORAGE_BASE}${url}`;
};
const getToken = () => localStorage.getItem('adminToken');
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

const ACCENT = 'linear-gradient(135deg,#533193,#7C3AED)';

/* ── Field group wrapper ───────────────────────────────────── */
const Section = ({ title, children }) => (
  <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
    {title && (
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/70">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</p>
      </div>
    )}
    <div className="p-5 space-y-4">{children}</div>
  </div>
);

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label}{required && <span className="text-red-400 ml-1">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition bg-white";
const textareaCls = `${inputCls} resize-none`;

/* ─── OVERVIEW FORM ───────────────────────────────────────── */
const OverviewTab = ({ yearData, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null); // { type: 'ok'|'err', text }
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  useEffect(() => {
    let highlightsStr = '';
    if (yearData.highlights) {
      try {
        const p = typeof yearData.highlights === 'string' ? JSON.parse(yearData.highlights) : yearData.highlights;
        if (Array.isArray(p)) highlightsStr = p.join('\n');
      } catch {}
    }
    setForm({ ...yearData, highlights: highlightsStr });
  }, [yearData]);

  const set = (name, value) => setForm(p => ({ ...p, [name]: value }));
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    set(name, type === 'checkbox' ? (checked ? 1 : 0) : value);
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    setUploading(true);
    try {
      const res = await axios.post(`${API_BASE}/upload`, fd, { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } });
      const url = res.data.fileUrl || res.data.url || res.data.s3Location;
      if (url) set('banner_url', url);
    } catch { setMsg({ type: 'err', text: 'อัปโหลดรูปไม่สำเร็จ' }); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const highlightsArr = form.highlights ? form.highlights.split('\n').map(s => s.trim()).filter(Boolean) : [];
      await axios.put(`${API_BASE}/symposium-years/admin/${yearData.id}`,
        { ...form, highlights: highlightsArr.length ? highlightsArr : null },
        { headers: authHeader() });
      setMsg({ type: 'ok', text: 'บันทึกสำเร็จแล้ว' });
      onSaved();
    } catch { setMsg({ type: 'err', text: 'บันทึกไม่สำเร็จ กรุณาลองใหม่' }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <AnimatePresence>
        {msg && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className={`flex items-center gap-2 p-3 rounded-xl text-sm font-medium ${
              msg.type === 'ok' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
            {msg.type === 'ok' ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> : <X className="w-4 h-4 flex-shrink-0" />}
            {msg.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Banner */}
      <Section title="Banner Image">
        <div className="flex gap-2 flex-wrap items-center">
          <input value={form.banner_url || ''} onChange={e => set('banner_url', e.target.value)}
            className={`${inputCls} flex-1 min-w-0`} placeholder="วาง URL หรือเลือกจาก Library..." />
          <button onClick={() => setShowMediaPicker(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-purple-300 text-purple-600 rounded-lg hover:bg-purple-50 transition whitespace-nowrap">
            <FolderOpen className="w-4 h-4" /> Library
          </button>
          <label className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition cursor-pointer whitespace-nowrap">
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> กำลังอัปโหลด...</> : <><Upload className="w-4 h-4" /> อัปโหลด</>}
            <input type="file" accept="image/*" className="hidden" onChange={handleBannerUpload} disabled={uploading} />
          </label>
        </div>
        {form.banner_url && (
          <div className="relative w-full h-36 rounded-xl overflow-hidden border border-gray-200 group mt-1">
            <img src={resolveImg(form.banner_url)} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
            <button onClick={() => set('banner_url', '')}
              className="absolute top-2 right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition opacity-0 group-hover:opacity-100">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <MediaPickerModal open={showMediaPicker} onClose={() => setShowMediaPicker(false)}
          onSelect={(url) => set('banner_url', url)} />
      </Section>

      {/* Identity */}
      <Section title="ข้อมูลพื้นฐาน">
        <div className="grid grid-cols-3 gap-4">
          <Field label="ปี" required>
            <input name="year" type="number" value={form.year || ''} onChange={handleChange} className={inputCls} />
          </Field>
          <Field label="ชื่องาน" required>
            <input name="title" value={form.title || ''} onChange={handleChange} className={inputCls} />
          </Field>
          <Field label="ลำดับแสดง">
            <input name="display_order" type="number" value={form.display_order ?? 0} onChange={handleChange} className={inputCls} />
          </Field>
        </div>
        <Field label="ชื่อรอง / Subtitle">
          <input name="subtitle" value={form.subtitle || ''} onChange={handleChange} className={inputCls} placeholder="งานวิชาการด้านงานศิลปหัตถกรรมไทย" />
        </Field>
        <Field label="หัวข้อหลัก (Theme)">
          <input name="theme" value={form.theme || ''} onChange={handleChange} className={inputCls} placeholder="ธีมของงาน..." />
        </Field>
        <Field label="รายละเอียด">
          <textarea name="description" value={form.description || ''} onChange={handleChange} rows={4} className={textareaCls} />
        </Field>
      </Section>

      {/* Event info */}
      <Section title="รายละเอียดการจัดงาน">
        <div className="grid grid-cols-2 gap-4">
          <Field label="วันที่จัดงาน">
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input name="event_date" value={form.event_date || ''} onChange={handleChange}
                className={`${inputCls} pl-9`} placeholder="15–17 มกราคม 2568" />
            </div>
          </Field>
          <Field label="สถานที่">
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input name="location" value={form.location || ''} onChange={handleChange}
                className={`${inputCls} pl-9`} placeholder="ไบเทค บางนา" />
            </div>
          </Field>
        </div>
        <Field label="ลิงก์ลงทะเบียน">
          <div className="relative">
            <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input name="register_url" value={form.register_url || ''} onChange={handleChange}
              className={`${inputCls} pl-9`} placeholder="https://symposium.sacit.or.th/register" />
          </div>
        </Field>
      </Section>

      {/* Highlights */}
      <Section title="ไฮไลท์ของงาน">
        <Field label="กรอกบรรทัดละ 1 รายการ">
          <textarea name="highlights" value={form.highlights || ''} onChange={handleChange} rows={5} className={textareaCls}
            placeholder={"การบรรยายพิเศษโดยผู้เชี่ยวชาญระดับนานาชาติ\nนิทรรศการผลงานศิลปหัตถกรรม\nเวิร์กชอปเทคนิคพื้นบ้าน"} />
        </Field>
      </Section>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <div className={`relative w-10 h-5 rounded-full transition-colors ${form.is_active ? 'bg-purple-600' : 'bg-gray-300'}`}
            onClick={() => set('is_active', form.is_active ? 0 : 1)}>
            <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.is_active ? 'translate-x-5' : ''}`} />
          </div>
          <span className="text-sm text-gray-700">{form.is_active ? 'แสดงบนเว็บไซต์' : 'ซ่อนจากเว็บไซต์'}</span>
        </label>
        <Button onClick={save} disabled={saving} className="text-white px-6 flex items-center gap-2" style={{ background: ACCENT }}>
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> กำลังบันทึก...</> : <><Save className="w-4 h-4" /> บันทึกข้อมูล</>}
        </Button>
      </div>
    </div>
  );
};

/* ─── VIDEOS TAB ──────────────────────────────────────────── */
const VideosTab = ({ yearId, videos: initVideos, onChanged }) => {
  const [videos, setVideos] = useState(initVideos || []);
  const [form, setForm] = useState({ title: '', description: '', video_url: '', thumbnail_url: '', display_order: 0, is_active: 1 });
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [msg, setMsg] = useState(null);
  const [showVideoPicker, setShowVideoPicker] = useState(false);

  const reload = useCallback(async () => {
    const res = await axios.get(`${API_BASE}/symposium-years/admin/detail/${yearId}`, { headers: authHeader() });
    setVideos(res.data.data?.videos || []);
    onChanged();
  }, [yearId, onChanged]);

  const openNew = () => { setForm({ title: '', description: '', video_url: '', thumbnail_url: '', display_order: 0, is_active: 1 }); setEditId(null); setShowForm(true); setMsg(null); };
  const openEdit = (v) => { setForm({ ...v }); setEditId(v.id); setShowForm(true); setMsg(null); };
  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const save = async () => {
    if (!form.video_url) { setMsg({ type: 'err', text: 'กรุณากรอก URL วิดีโอ' }); return; }
    setSaving(true); setMsg(null);
    try {
      if (editId) await axios.put(`${API_BASE}/symposium-years/admin/videos/${editId}`, form, { headers: authHeader() });
      else await axios.post(`${API_BASE}/symposium-years/admin/${yearId}/videos`, form, { headers: authHeader() });
      setShowForm(false); reload();
    } catch { setMsg({ type: 'err', text: 'บันทึกไม่สำเร็จ' }); }
    finally { setSaving(false); }
  };

  const handleVideoFromLibrary = async (videoData) => {
    try {
      await axios.post(`${API_BASE}/symposium-years/admin/${yearId}/videos`,
        { title: videoData.title, video_url: videoData.video_url, thumbnail_url: videoData.thumbnail_url || '', display_order: videos.length, is_active: 1 },
        { headers: authHeader() });
      setMsg({ type: 'ok', text: 'เพิ่มวิดีโอจาก Library แล้ว' });
      reload();
    } catch { setMsg({ type: 'err', text: 'เพิ่มวิดีโอไม่สำเร็จ' }); }
  };

  const del = async () => {
    await axios.delete(`${API_BASE}/symposium-years/admin/videos/${deleteId}`, { headers: authHeader() });
    setDeleteId(null); reload();
  };

  const ytThumb = (url) => {
    try {
      const u = new URL(url);
      const vid = u.searchParams.get('v') || (u.hostname === 'youtu.be' ? u.pathname.slice(1) : null);
      if (vid) return `https://img.youtube.com/vi/${vid}/mqdefault.jpg`;
    } catch {} return null;
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{videos.length} รายการ</p>
        <div className="flex gap-2">
          <button onClick={() => setShowVideoPicker(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-purple-300 text-purple-600 rounded-lg hover:bg-purple-50 transition">
            <Film className="w-4 h-4" /> เลือกจาก Library
          </button>
          <Button onClick={openNew} size="sm" className="text-white flex items-center gap-1.5" style={{ background: ACCENT }}>
            <Plus className="w-4 h-4" /> เพิ่ม URL ใหม่
          </Button>
        </div>
      </div>

      <MediaPickerModal open={showVideoPicker} onClose={() => setShowVideoPicker(false)}
        onSelectVideo={handleVideoFromLibrary} mediaType="video" />

      <AnimatePresence>
        {msg && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className={`flex items-center gap-2 p-3 rounded-xl text-sm ${msg.type === 'ok' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {msg.type === 'ok' ? <CheckCircle2 className="w-4 h-4" /> : <X className="w-4 h-4" />} {msg.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inline form */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            <Section title={editId ? 'แก้ไขวิดีโอ' : 'เพิ่มวิดีโอใหม่'}>
              <div className="flex items-center justify-between -mt-1 mb-2">
                <span />
                <button onClick={() => setShowForm(false)}><X className="w-4 h-4 text-gray-400 hover:text-gray-600" /></button>
              </div>
              <Field label="URL วิดีโอ" required>
                <input name="video_url" value={form.video_url} onChange={handleChange} className={inputCls}
                  placeholder="https://youtube.com/watch?v=..." />
                <p className="text-xs text-gray-400 mt-1">รองรับ YouTube, Vimeo หรือ direct video URL</p>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="ชื่อวิดีโอ">
                  <input name="title" value={form.title} onChange={handleChange} className={inputCls} />
                </Field>
                <Field label="ลำดับ">
                  <input name="display_order" type="number" value={form.display_order} onChange={handleChange} className={inputCls} />
                </Field>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>ยกเลิก</Button>
                <Button size="sm" onClick={save} disabled={saving} className="text-white" style={{ background: ACCENT }}>
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> บันทึก...</> : <><Save className="w-4 h-4" /> บันทึก</>}
                </Button>
              </div>
            </Section>
          </motion.div>
        )}
      </AnimatePresence>

      {/* List */}
      {videos.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border-2 border-dashed border-gray-200">
          <Play className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-gray-400">ยังไม่มีวิดีโอ เพิ่มจาก Library หรือกรอก URL</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {videos.map((v) => {
            const thumb = v.thumbnail_url ? resolveImg(v.thumbnail_url) : ytThumb(v.video_url);
            return (
              <motion.div key={v.id} layout
                className="group flex gap-3 p-3 bg-white rounded-xl border border-gray-200 hover:border-purple-200 hover:shadow-sm transition-all">
                <div className="w-28 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-900 relative">
                  {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center"><Play className="w-5 h-5 text-white/30" /></div>}
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                    <div className="w-6 h-6 rounded-full bg-white/80 flex items-center justify-center shadow-sm">
                      <Play className="w-3 h-3 text-red-600 fill-red-600 ml-0.5" />
                    </div>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 line-clamp-1">{v.title || '(ไม่มีชื่อ)'}</p>
                  <p className="text-xs text-gray-400 truncate mt-0.5">{v.video_url}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(v)} className="p-1.5 text-gray-400 hover:text-blue-600 transition rounded-lg hover:bg-blue-50">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setDeleteId(v.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition rounded-lg hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการลบวิดีโอ</AlertDialogTitle>
            <AlertDialogDescription>ลบวิดีโอนี้ออกจากระบบ?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">ลบ</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/* ─── PHOTOS TAB ──────────────────────────────────────────── */
const PhotosTab = ({ yearId, photos: initPhotos, onChanged }) => {
  const [photos, setPhotos] = useState(initPhotos || []);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editPhoto, setEditPhoto] = useState(null);
  const [msg, setMsg] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const reload = useCallback(async () => {
    const res = await axios.get(`${API_BASE}/symposium-years/admin/detail/${yearId}`, { headers: authHeader() });
    setPhotos(res.data.data?.photos || []);
    onChanged();
  }, [yearId, onChanged]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true); setMsg(null);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData(); fd.append('file', file);
        const res = await axios.post(`${API_BASE}/upload`, fd, { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } });
        const url = res.data.fileUrl || res.data.url || res.data.s3Location;
        if (url) uploaded.push({ photo_url: url, caption: '', display_order: photos.length + uploaded.length });
      }
      if (uploaded.length) {
        await axios.post(`${API_BASE}/symposium-years/admin/${yearId}/photos`, { photos: uploaded }, { headers: authHeader() });
        setMsg({ type: 'ok', text: `อัปโหลดสำเร็จ ${uploaded.length} ภาพ` });
        reload();
      }
    } catch { setMsg({ type: 'err', text: 'อัปโหลดบางรูปไม่สำเร็จ' }); }
    finally { setUploading(false); }
  };

  const addByUrl = async () => {
    if (!urlInput.trim()) return;
    try {
      await axios.post(`${API_BASE}/symposium-years/admin/${yearId}/photos`,
        { photos: [{ photo_url: urlInput.trim(), caption: '', display_order: photos.length }] },
        { headers: authHeader() });
      setUrlInput(''); setShowUrlInput(false); reload();
    } catch { setMsg({ type: 'err', text: 'เพิ่ม URL ไม่สำเร็จ' }); }
  };

  const saveCaption = async () => {
    if (!editPhoto) return;
    await axios.put(`${API_BASE}/symposium-years/admin/photos/${editPhoto.id}`,
      { photo_url: editPhoto.photo_url, caption: editPhoto.caption, display_order: editPhoto.display_order, is_active: editPhoto.is_active ?? 1 },
      { headers: authHeader() });
    setEditPhoto(null); reload();
  };

  const del = async () => {
    await axios.delete(`${API_BASE}/symposium-years/admin/photos/${deleteId}`, { headers: authHeader() });
    setDeleteId(null); reload();
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-gray-500">{photos.length} รูป</p>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowMediaPicker(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-purple-300 text-purple-600 rounded-lg hover:bg-purple-50 transition">
            <FolderOpen className="w-4 h-4" /> เลือกจาก Library
          </button>
          <button onClick={() => setShowUrlInput(!showUrlInput)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition">
            <Plus className="w-4 h-4" /> เพิ่ม URL
          </button>
          <label className="flex items-center gap-1.5 px-3 py-2 text-sm text-white rounded-lg cursor-pointer transition" style={{ background: ACCENT }}>
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> กำลังอัปโหลด...</> : <><Upload className="w-4 h-4" /> อัปโหลดรูป</>}
            <input type="file" multiple accept="image/*" className="hidden" onChange={handleFileUpload} disabled={uploading} />
          </label>
        </div>
      </div>

      <MediaPickerModal open={showMediaPicker} onClose={() => setShowMediaPicker(false)}
        onSelectMultiple={async (urls) => {
          try {
            const list = urls.map((u, i) => ({ photo_url: u, caption: '', display_order: photos.length + i }));
            await axios.post(`${API_BASE}/symposium-years/admin/${yearId}/photos`, { photos: list }, { headers: authHeader() });
            setMsg({ type: 'ok', text: `เพิ่มรูปจาก Library ${urls.length} รูป` });
            reload();
          } catch { setMsg({ type: 'err', text: 'เพิ่มรูปไม่สำเร็จ' }); }
        }} />

      <AnimatePresence>
        {msg && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className={`flex items-center gap-2 p-3 rounded-xl text-sm ${msg.type === 'ok' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {msg.type === 'ok' ? <CheckCircle2 className="w-4 h-4" /> : <X className="w-4 h-4" />} {msg.text}
          </motion.div>
        )}
        {showUrlInput && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="flex gap-2 p-3 bg-purple-50 rounded-xl border border-purple-200">
            <input value={urlInput} onChange={e => setUrlInput(e.target.value)} placeholder="https://... URL รูปภาพ"
              className={`${inputCls} flex-1`} onKeyDown={e => e.key === 'Enter' && addByUrl()} />
            <Button size="sm" onClick={addByUrl} className="text-white" style={{ background: ACCENT }}>เพิ่ม</Button>
            <button onClick={() => setShowUrlInput(false)}><X className="w-4 h-4 text-gray-400" /></button>
          </motion.div>
        )}
        {editPhoto && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="flex gap-3 items-center p-3 bg-purple-50 rounded-xl border border-purple-200">
            <img src={resolveImg(editPhoto.photo_url)} alt="" className="w-16 h-11 rounded-lg object-cover flex-shrink-0 border border-purple-200" />
            <input value={editPhoto.caption || ''} onChange={e => setEditPhoto(p => ({ ...p, caption: e.target.value }))}
              placeholder="คำบรรยายภาพ..." className={`${inputCls} flex-1`} />
            <Button size="sm" onClick={saveCaption} className="text-white" style={{ background: ACCENT }}><Save className="w-4 h-4" /></Button>
            <button onClick={() => setEditPhoto(null)}><X className="w-4 h-4 text-gray-400" /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {photos.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border-2 border-dashed border-gray-200">
          <Image className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-gray-400">ยังไม่มีภาพ อัปโหลดหรือเลือกจาก Library</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
          {photos.map((p) => (
            <div key={p.id} className="group relative rounded-xl overflow-hidden aspect-square bg-gray-100 border border-gray-200">
              <img src={resolveImg(p.photo_url)} alt={p.caption || ''} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors" />
              <div className="absolute inset-0 flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setEditPhoto(p)} className="p-1.5 bg-white rounded-lg text-gray-700 hover:bg-white shadow-sm transition">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setDeleteId(p.id)} className="p-1.5 bg-white rounded-lg text-red-600 hover:bg-white shadow-sm transition">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {p.caption && (
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 px-2 py-1.5">
                  <p className="text-white text-xs truncate">{p.caption}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการลบภาพ</AlertDialogTitle>
            <AlertDialogDescription>ลบภาพนี้ออกจากระบบ?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">ลบ</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/* ─── YEAR EDITOR ─────────────────────────────────────────── */
const YearEditor = ({ yearId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/symposium-years/admin/detail/${yearId}`, { headers: authHeader() });
      setData(res.data.data);
    } catch {}
    finally { setLoading(false); }
  }, [yearId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /></div>;
  if (!data) return <div className="text-center py-24 text-gray-400">ไม่พบข้อมูล</div>;

  const tabs = [
    { id: 'overview', label: 'ภาพรวม', icon: Info, count: null },
    { id: 'videos',   label: 'วิดีโอ',  icon: Play,  count: data.videos?.length || 0 },
    { id: 'photos',   label: 'ภาพถ่าย', icon: Image, count: data.photos?.length || 0 },
  ];

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition shadow-sm">
          <ArrowLeft className="w-4 h-4" /> กลับ
        </button>

        {data.banner_url && (
          <div className="w-12 h-8 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200 shadow-sm">
            <img src={resolveImg(data.banner_url)} alt="" className="w-full h-full object-cover" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-gray-900 truncate">{data.title}</h2>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${data.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
              {data.is_active ? 'แสดงอยู่' : 'ซ่อน'}
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
            {data.event_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{data.event_date}</span>}
            {data.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{data.location}</span>}
          </div>
        </div>

        <a href={`/symposium/${data.year}`} target="_blank" rel="noopener noreferrer"
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 text-sm text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition">
          <Globe className="w-4 h-4" /> ดูหน้าสาธารณะ
        </a>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-6 w-fit">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition ${
              tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            <t.icon className="w-4 h-4" />
            {t.label}
            {t.count !== null && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-purple-100 text-purple-700' : 'bg-gray-200 text-gray-500'}`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
        {tab === 'overview' && <OverviewTab yearData={data} onSaved={load} />}
        {tab === 'videos'   && <VideosTab yearId={data.id} videos={data.videos || []} onChanged={load} />}
        {tab === 'photos'   && <PhotosTab yearId={data.id} photos={data.photos || []} onChanged={load} />}
      </motion.div>
    </div>
  );
};

/* ─── CREATE YEAR DRAWER ──────────────────────────────────── */
const EMPTY_FORM = { year: '', title: '', subtitle: '', is_active: 1, display_order: 0 };

const CreateDrawer = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (open) { setForm(EMPTY_FORM); setError(''); } }, [open]);
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
  };
  const set = (name, val) => setForm(p => ({ ...p, [name]: val }));

  const submit = async () => {
    if (!form.year || !form.title) { setError('กรุณากรอก ปี และ ชื่องาน'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API_BASE}/symposium-years/admin`, form, { headers: authHeader() });
      onCreated(); onClose();
    } catch { setError('สร้างไม่สำเร็จ'); }
    finally { setSaving(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 z-40 bg-black/30" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} />
          <motion.div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col"
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b" style={{ fontFamily: 'Prompt' }}>
              <div>
                <h2 className="text-lg font-bold text-gray-900">สร้าง Symposium Year ใหม่</h2>
                <p className="text-xs text-gray-400 mt-0.5">กรอกข้อมูลพื้นฐาน แก้ไขเพิ่มเติมได้ภายหลัง</p>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5" style={{ fontFamily: 'Prompt' }}>
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
                  <X className="w-4 h-4 flex-shrink-0" /> {error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <Field label="ปี" required>
                  <input name="year" type="number" value={form.year} onChange={handleChange}
                    className={inputCls} placeholder="2026" />
                </Field>
                <Field label="ลำดับแสดง">
                  <input name="display_order" type="number" value={form.display_order} onChange={handleChange} className={inputCls} />
                </Field>
              </div>

              <Field label="ชื่องาน" required>
                <input name="title" value={form.title} onChange={handleChange}
                  className={inputCls} placeholder="SACIT Symposium 2026" />
              </Field>

              <Field label="ชื่อรอง / Subtitle">
                <input name="subtitle" value={form.subtitle} onChange={handleChange}
                  className={inputCls} placeholder="งานวิชาการด้านงานศิลปหัตถกรรมไทย" />
              </Field>

              <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className={`relative w-10 h-5 rounded-full transition-colors ${form.is_active ? 'bg-purple-600' : 'bg-gray-300'}`}
                    onClick={() => set('is_active', form.is_active ? 0 : 1)}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.is_active ? 'translate-x-5' : ''}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">แสดงบนเว็บไซต์</p>
                    <p className="text-xs text-gray-400">จะปรากฏใน Navbar dropdown ทันที</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex gap-3" style={{ fontFamily: 'Prompt' }}>
              <Button variant="outline" className="flex-1" onClick={onClose}>ยกเลิก</Button>
              <Button onClick={submit} disabled={saving} className="flex-1 text-white flex items-center justify-center gap-2" style={{ background: ACCENT }}>
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> กำลังสร้าง...</> : <><Sparkles className="w-4 h-4" /> สร้างเลย</>}
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

/* ─── MAIN PAGE ───────────────────────────────────────────── */
const SymposiumYearsAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingYearId, setEditingYearId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/symposium-years/admin/list`, { headers: authHeader() });
      setItems(res.data.data || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async () => {
    await axios.delete(`${API_BASE}/symposium-years/admin/${deleteId}`, { headers: authHeader() });
    setDeleteId(null); load();
  };

  if (editingYearId) {
    return (
      <div className="p-6 max-w-4xl mx-auto" style={{ fontFamily: 'Prompt' }}>
        <YearEditor yearId={editingYearId} onBack={() => { setEditingYearId(null); load(); }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50" style={{ fontFamily: 'Prompt' }}>
      <div className="max-w-5xl mx-auto px-6 py-8">

        {/* Page header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: ACCENT }}>
                <Calendar className="w-4 h-4" />
              </div>
              <span className="text-xs font-semibold text-purple-600 uppercase tracking-wider">SACIT Symposium</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">จัดการ Symposium Years</h1>
            <p className="text-sm text-gray-500 mt-1">เนื้อหา วิดีโอ และภาพบรรยากาศของแต่ละปี</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="text-white flex items-center gap-2 shadow-md" style={{ background: ACCENT }}>
            <Plus className="w-4 h-4" /> สร้างปีใหม่
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 rounded-2xl border-2 border-dashed border-gray-200 bg-white">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 font-medium">ยังไม่มีข้อมูล</p>
            <p className="text-sm text-gray-400 mt-1">กดปุ่ม "สร้างปีใหม่" เพื่อเริ่มต้น</p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item, i) => (
              <motion.div key={item.id}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="group bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all overflow-hidden">
                <div className="flex">
                  {/* Banner / Year */}
                  <div className="w-40 flex-shrink-0 relative overflow-hidden" style={{ minHeight: '120px' }}>
                    {item.banner_url ? (
                      <img src={resolveImg(item.banner_url)} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center" style={{ background: ACCENT }}>
                        <span className="text-white text-4xl font-black opacity-80">{item.year}</span>
                      </div>
                    )}
                    {item.banner_url && (
                      <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent flex items-center justify-center">
                        <span className="text-white text-3xl font-black drop-shadow-lg">{item.year}</span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-5 flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-gray-900 text-lg leading-tight">{item.title}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                              {item.is_active ? '● แสดงอยู่' : '○ ซ่อน'}
                            </span>
                          </div>
                          {item.subtitle && <p className="text-sm text-gray-500 mt-1">{item.subtitle}</p>}
                          {item.theme && <p className="text-sm text-purple-600 italic mt-1">"{item.theme}"</p>}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-4 text-xs text-gray-400 mt-3">
                        {item.event_date && (
                          <span className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-full">
                            <Calendar className="w-3 h-3" /> {item.event_date}
                          </span>
                        )}
                        {item.location && (
                          <span className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-full">
                            <MapPin className="w-3 h-3" /> {item.location}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <a href={`/symposium/${item.year}`} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 hover:text-purple-600 transition">
                          <Globe className="w-3.5 h-3.5" /> ดูหน้าสาธารณะ
                        </a>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setDeleteId(item.id)}
                          className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition">
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingYearId(item.id)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-white rounded-lg shadow-sm transition hover:opacity-90"
                          style={{ background: ACCENT }}>
                          <Pencil className="w-3.5 h-3.5" /> จัดการเนื้อหา <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Create Drawer */}
      <CreateDrawer open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการลบ</AlertDialogTitle>
            <AlertDialogDescription>ลบ Symposium Year นี้ รวมถึงวิดีโอและภาพทั้งหมดที่เกี่ยวข้องด้วย?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">ลบออก</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SymposiumYearsAdmin;
